import React, { useState, useRef, useEffect } from "react";
import { Globe, ChevronDown, Check } from "lucide-react";

const LANGUAGES = [
  { code: "en", label: "English", native: "English", flag: "EN" },
  { code: "hi", label: "Hindi", native: "हिन्दी", flag: "HI" },
  { code: "es", label: "Spanish", native: "Español", flag: "ES" },
  { code: "fr", label: "French", native: "Français", flag: "FR" },
  { code: "de", label: "German", native: "Deutsch", flag: "DE" },
  { code: "ar", label: "Arabic", native: "العربية", flag: "AR" },
  { code: "zh", label: "Chinese", native: "中文", flag: "ZH" },
];

export default function LanguageSwitcher({ variant = "dark" }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("lang") || "en";
    }
    return "en";
  });
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const current = LANGUAGES.find((l) => l.code === active) || LANGUAGES[0];

  const select = (code) => {
    setActive(code);
    localStorage.setItem("lang", code);
    setOpen(false);
  };

  const isDark = variant === "dark";

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-full border transition-colors text-xs font-semibold tracking-wider uppercase ${
          isDark
            ? "border-neutral-700 hover:border-orange-500 text-neutral-200 hover:text-orange-400 bg-neutral-800/50"
            : "border-neutral-300 hover:border-orange-500 text-neutral-800 hover:text-orange-500 bg-white"
        }`}
        aria-label="Select language"
      >
        <Globe size={14} className="text-orange-500" />
        <span>{current.flag}</span>
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-neutral-200 py-2 z-[60] overflow-hidden">
          <p className="px-4 pt-1 pb-2 text-[10px] uppercase tracking-widest text-neutral-400 font-semibold border-b border-neutral-100">
            Select Language
          </p>
          <div className="max-h-72 overflow-y-auto">
            {LANGUAGES.map((l) => (
              <button
                key={l.code}
                onClick={() => select(l.code)}
                className={`w-full flex items-center justify-between gap-3 px-4 py-2 text-sm transition-colors ${
                  active === l.code ? "bg-orange-50 text-orange-600" : "text-neutral-800 hover:bg-neutral-50"
                }`}
              >
                <span className="flex items-center gap-3">
                  <span className="w-7 h-7 rounded-full bg-orange-100 text-orange-600 text-[10px] font-bold flex items-center justify-center">
                    {l.flag}
                  </span>
                  <span className="flex flex-col text-left leading-tight">
                    <span className="font-semibold">{l.label}</span>
                    <span className="text-xs text-neutral-500">{l.native}</span>
                  </span>
                </span>
                {active === l.code && <Check size={14} className="text-orange-500" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
